// NDIS Funding Plans (Brevity "Client Funding record" parity). A plan = source + pricelist +
// period + total budget; under it are service lines (per service type) with optional budget and
// Allocated/Utilised/Balance. Each line is backed by a client_funds row so scheduling/invoicing/
// timesheets/reports draw from the right budget automatically.
import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Plus, Loader2, X, ChevronRight, Trash2, Lock, Unlock, FileText, Pencil, AlertTriangle, Calendar, Check, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { money, fmtDate } from '../../components/ui';
import SilWeeklyDialog from './SilWeeklyDialog';
// Money is written by the app-wide money() ('$1,234.50' / '-$2,556.70'); amounts are unchanged.
// Always surface the real reason an action failed (backend message → error detail → JS error).
const errMsg = (e, fb = 'Failed') => e?.response?.data?.message || e?.response?.data?.error || e?.message || fb;
const SOURCES = [['ndis', 'NDIS'], ['hcp', 'Home Care Package'], ['chsp', 'CHSP'], ['plan_cash', 'Plan Managed'], ['self', 'Self Managed'], ['other', 'Other']];
// Colour a service by its NDIS budget category (used when the line isn't tied to a service type).
const CAT_COLOR = { daily_activities: '#10b981', social_community: '#6366f1', transport: '#f59e0b', consumables: '#06b6d4', capacity_building: '#a855f7', capital: '#ec4899', other: '#94a3b8' };
const RELEASE = [['none', 'None'], ['1m', 'Monthly'], ['3m', 'Quarterly'], ['6m', 'Half-yearly'], ['12m', 'Annual']];
// How the plan is managed — drives billing. (Distinct from the funding source above.)
const MANAGED = [['ndia_managed', 'NDIA-managed'], ['plan_managed', 'Plan-managed'], ['self_managed', 'Self-managed']];
const MANAGED_LABEL = Object.fromEntries(MANAGED);
const MANAGED_NOTE = {
  ndia_managed: 'Billed to the NDIA via claim (PRODA / bulk payment request).',
  plan_managed: 'Invoiced to the plan manager, then synced to Xero.',
  self_managed: 'Invoiced to the participant, then synced to Xero.',
};

export default function ClientFundingPlans({ clientId, client }) {
  const confirm = useConfirm();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);      // plan detail (full object) | null
  const [createOpen, setCreateOpen] = useState(false);
  const [priceBooks, setPriceBooks] = useState([]);
  const [legacy, setLegacy] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/funding/plans?clientId=${clientId}`); setPlans(data || []); }
    catch { /* table may be new */ }
    try { const { data } = await api.get(`/funding/legacy-funds?clientId=${clientId}`); setLegacy(data || []); } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); api.get('/price-books').then(r => setPriceBooks(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [load]);

  const openPlan = async (id) => { try { const { data } = await api.get(`/funding/plans/${id}`); setOpen(data); } catch (e) { toast.error(errMsg(e, 'Failed to open plan')); } };
  const delLegacy = async (f) => {
    if (!await confirm(`Delete fund "${f.fund_name}"? This cannot be undone.`, { title: 'Delete Fund', danger: true })) return;
    try { await api.delete(`/clients/${clientId}/funds/${f.id}`); toast.success('Fund deleted'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
        <Wallet size={15} className="text-slate-400" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-700">NDIS funding plans</h3>
        <button type="button" onClick={() => setCreateOpen(true)} className="ml-auto inline-flex items-center gap-1.5 h-9 px-2.5 -my-1.5 rounded-lg text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100"><Plus size={14} aria-hidden /> Add</button>
      </div>
      <div className="p-5 space-y-2">
        {plans.length === 0 ? (
          <p className="text-sm text-slate-500">No funding plans yet. A plan holds the price list, total budget and period. Add service-line budgets so each service bills against the right budget.</p>
        ) : plans.map(p => (
          <button type="button" key={p.id} onClick={() => openPlan(p.id)} className="w-full text-left rounded-lg border border-slate-200 p-3 hover:border-slate-300 hover:bg-slate-50/60 transition-colors duration-150 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 text-sm">{(SOURCES.find(s => s[0] === p.funding_source) || [])[1] || p.funding_source} · {p.price_book_name || 'no pricelist'}</p>
              <p className="text-xs text-slate-500">{p.start_date ? fmtDate(String(p.start_date).slice(0, 10)) : '…'} – {p.end_date ? fmtDate(String(p.end_date).slice(0, 10)) : 'open'} · {p.line_count} service{Number(p.line_count) === 1 ? '' : 's'}{p.is_published ? '' : ' · draft'}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900 tabular-nums">{money(p.balance)}</p>
              <p className="text-[11px] text-slate-500 tabular-nums">of {money(p.budget)}</p>
            </div>
            <ChevronRight size={15} className="text-slate-400" aria-hidden />
          </button>
        ))}
      </div>

      {legacy.length > 0 && (
        <div className="px-5 pb-4">
          <p className="text-[12px] font-semibold text-slate-600 mb-1.5">Other funds (not in a plan)</p>
          <div className="space-y-1">
            {legacy.map(f => (
              <div key={f.id} className="flex flex-wrap items-center gap-2 text-xs rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-1.5">
                <span className="font-medium text-slate-600 flex-1">{f.fund_name}{f.orphaned && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">orphaned</span>}</span>
                {f.support_category && <span className="text-slate-500">{f.support_category.replace(/_/g, ' ')}</span>}
                <span className="text-slate-600 tabular-nums">{money((Number(f.fund_amount) || 0) - (Number(f.spent_amount) || 0))} left</span>
                <button type="button" onClick={() => delLegacy(f)} title="Delete fund" aria-label={`Delete fund ${f.fund_name}`} className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={13} aria-hidden /></button>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-slate-500 mt-1.5">These older funds still work for billing. Create a plan above to manage budgets the new way.</p>
        </div>
      )}

      {createOpen && <PlanModal clientId={clientId} client={client} priceBooks={priceBooks} onClose={() => setCreateOpen(false)} onSaved={(p) => { setCreateOpen(false); load(); openPlan(p.id); }} />}
      {open && <PlanDetail plan={open} priceBooks={priceBooks} confirm={confirm} onClose={() => setOpen(null)} onChanged={(p) => setOpen(p)} onReload={load} />}
    </div>
  );
}

function PlanModal({ clientId, client, priceBooks, onClose, onSaved }) {
  const [form, setForm] = useState({ fundingSource: 'ndis', managedBy: 'ndia_managed', priceBookId: '', registrationNumber: client?.ndis_number || '', startDate: '', endDate: '', releasePeriod: 'none', budget: '', invoiceTo: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.budget) return toast.error('Total budget required');
    setSaving(true);
    try { const { data } = await api.post('/funding/plans', { clientId, ...form }); toast.success('Plan created'); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="New Funding Plan" onClose={onClose}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Funding Source"><select className="input-field" value={form.fundingSource} onChange={e => set('fundingSource', e.target.value)}>{SOURCES.map(s => <option key={s[0]} value={s[0]}>{s[1]}</option>)}</select></F>
        <F label="Pricelist (price book)"><select className="input-field" value={form.priceBookId} onChange={e => set('priceBookId', e.target.value)}><option value="">— Client default —</option>{priceBooks.map(pb => <option key={pb.id} value={pb.id}>{pb.name}</option>)}</select></F>
      </div>
      <F label="Managed by" hint={MANAGED_NOTE[form.managedBy]}>
        <select className="input-field" value={form.managedBy} onChange={e => set('managedBy', e.target.value)}>{MANAGED.map(m => <option key={m[0]} value={m[0]}>{m[1]}</option>)}</select>
      </F>
      {form.managedBy !== 'ndia_managed' && (
        <F label={form.managedBy === 'plan_managed' ? 'Plan manager (invoice to)' : 'Invoice to'} hint="Who the invoice is addressed to.">
          <input className="input-field" value={form.invoiceTo} onChange={e => set('invoiceTo', e.target.value)} placeholder={form.managedBy === 'plan_managed' ? 'Plan management company / email' : 'Participant / nominee'} />
        </F>
      )}
      <F label="NDIS / Registration Number"><input className="input-field" value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} /></F>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Start Date"><input type="date" className="input-field" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></F>
        <F label="End Date"><input type="date" className="input-field" value={form.endDate} onChange={e => set('endDate', e.target.value)} /></F>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Total Budget ($)"><input type="number" className="input-field" value={form.budget} onChange={e => set('budget', e.target.value)} /></F>
        <F label="Release Period"><select className="input-field" value={form.releasePeriod} onChange={e => set('releasePeriod', e.target.value)}>{RELEASE.map(r => <option key={r[0]} value={r[0]}>{r[1]}</option>)}</select></F>
      </div>
      <div className="flex justify-end gap-2 pt-2"><button onClick={onClose} className="btn-secondary btn-sm">Cancel</button><button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Creating…' : 'Create plan'}</button></div>
    </Modal>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    rose:  'bg-rose-50 border-rose-200 text-rose-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${tones[tone] || tones.slate}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</p>
      <p className="text-base font-bold leading-tight mt-0.5">{value}</p>
    </div>
  );
}

const fmtD = (d) => (d ? String(d).slice(0, 10) : null);

function PlanDetail({ plan, priceBooks, confirm, onClose, onChanged, onReload }) {
  const s = plan.summary || {};
  const [addOpen, setAddOpen] = useState(false);
  const [editBudget, setEditBudget] = useState(null); // null = view; string = editing the plan total
  const [editDates, setEditDates] = useState(null);   // null = view; {start,end} = editing the plan period
  const reloadPlan = async () => { const { data } = await api.get(`/funding/plans/${plan.id}`); onChanged(data); onReload(); };
  const savePlanBudget = async () => { try { await api.put(`/funding/plans/${plan.id}`, { budget: Number(editBudget) || 0 }); setEditBudget(null); reloadPlan(); } catch (e) { toast.error(errMsg(e)); } };
  const savePlanDates = async () => { try { await api.put(`/funding/plans/${plan.id}`, { startDate: editDates.start || null, endDate: editDates.end || null }); setEditDates(null); reloadPlan(); } catch (e) { toast.error(errMsg(e)); } };

  const publish = async () => { try { await api.post(`/funding/plans/${plan.id}/publish`, { publish: !plan.is_published }); reloadPlan(); } catch (e) { toast.error(errMsg(e)); } };
  const delLine = async (l) => { if (!await confirm(`Remove "${l.service_type_name}"?`, { danger: true })) return; try { await api.delete(`/funding/plans/${plan.id}/lines/${l.id}`); reloadPlan(); } catch (e) { toast.error(errMsg(e)); } };
  const deletePlan = async () => { if (!await confirm('Delete this funding plan and its budgets? (Blocked if anything has been utilised.)', { title: 'Delete Plan', danger: true })) return; try { await api.delete(`/funding/plans/${plan.id}`); toast.success('Plan deleted'); onClose(); onReload(); } catch (e) { toast.error(errMsg(e)); } };

  const overAllocated = (Number(s.allocated) || 0) > (Number(s.budget) || 0) + 0.001;

  return (
    <Modal title="Funding Plan" onClose={onClose} xwide>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-xl border px-3 py-2.5 bg-slate-50 border-slate-200 text-slate-700">
          <p className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">Budget</p>
          {editBudget == null ? (
            <button onClick={() => setEditBudget(String(s.budget ?? ''))} title="Edit total budget" className="text-base font-bold leading-tight mt-0.5 flex items-center gap-1 hover:text-indigo-600">
              {money(s.budget)} <Pencil size={11} className="opacity-40" />
            </button>
          ) : (
            <div className="flex items-center gap-1 mt-0.5">
              <input type="number" autoFocus className="w-20 border border-indigo-300 rounded px-1.5 py-0.5 text-sm font-bold" value={editBudget}
                onChange={e => setEditBudget(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') savePlanBudget(); if (e.key === 'Escape') setEditBudget(null); }} />
              <button onClick={savePlanBudget} className="text-emerald-600"><Check size={15} /></button>
              <button onClick={() => setEditBudget(null)} className="text-slate-400"><X size={15} /></button>
            </div>
          )}
        </div>
        <Stat label="Allocated" value={money(s.allocated)} tone={overAllocated ? 'rose' : 'amber'} />
        <Stat label="Utilised" value={money(s.utilised)} tone="rose" />
        <Stat label="Balance" value={money(s.balance)} tone="emerald" />
      </div>
      {overAllocated && (
        <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
          <AlertTriangle size={13} className="shrink-0" /> Over-allocated by {money((Number(s.allocated) || 0) - (Number(s.budget) || 0))} — the service budgets exceed the plan total. Reduce a service budget or raise the plan total.
        </div>
      )}

      {/* Meta + actions */}
      <div className="flex items-center gap-3 flex-wrap mt-3">
        <span className="text-xs text-slate-500 flex items-center gap-1.5"><FileText size={12} className="text-slate-400" />{plan.price_book_name || 'client default pricelist'}</span>
        {editDates == null ? (
          <button onClick={() => setEditDates({ start: fmtD(plan.start_date) || '', end: fmtD(plan.end_date) || '' })} title="Edit funding period" className="text-xs text-slate-500 flex items-center gap-1.5 hover:text-indigo-600">
            <Calendar size={12} className="text-slate-400" />{fmtD(plan.start_date) || '—'} → {fmtD(plan.end_date) || 'open'} <Pencil size={10} className="opacity-40" />
          </button>
        ) : (
          <span className="flex items-center gap-1 text-xs">
            <Calendar size={12} className="text-slate-400" />
            <input type="date" className="border border-indigo-300 rounded px-1.5 py-0.5 text-xs" value={editDates.start} onChange={e => setEditDates(d => ({ ...d, start: e.target.value }))} />
            <span className="text-slate-400">→</span>
            <input type="date" className="border border-indigo-300 rounded px-1.5 py-0.5 text-xs" value={editDates.end} onChange={e => setEditDates(d => ({ ...d, end: e.target.value }))} />
            <button onClick={savePlanDates} className="text-emerald-600"><Check size={14} /></button>
            <button onClick={() => setEditDates(null)} className="text-slate-400"><X size={14} /></button>
          </span>
        )}
        <span className="text-xs text-slate-500 flex items-center gap-1.5" title={MANAGED_NOTE[plan.managed_by || 'ndia_managed']}>
          <Wallet size={12} className="text-slate-400" />
          <select className="bg-transparent text-xs text-slate-600 font-medium border border-slate-200 rounded px-1 py-0.5 hover:border-indigo-300 cursor-pointer"
            value={plan.managed_by || 'ndia_managed'}
            onChange={async e => { try { await api.put(`/funding/plans/${plan.id}`, { managedBy: e.target.value }); reloadPlan(); } catch (err) { toast.error(errMsg(err)); } }}>
            {MANAGED.map(m => <option key={m[0]} value={m[0]}>{m[1]}</option>)}
          </select>
        </span>
        <span className="text-xs text-slate-500 flex items-center gap-1.5" title={plan.is_published ? 'Locked — set the plan back to Draft to change how the budget is released' : 'How often the plan budget is released — change it any time to re-split the remaining installments'}>
          {plan.is_published ? <Lock size={12} className="text-slate-400" /> : <RefreshCw size={12} className="text-slate-400" />}
          <select className={`bg-transparent text-xs font-medium border rounded px-1 py-0.5 ${plan.is_published ? 'text-slate-400 border-slate-100 cursor-not-allowed' : 'text-slate-600 border-slate-200 hover:border-indigo-300 cursor-pointer'}`}
            value={plan.release_period || 'none'}
            disabled={plan.is_published}
            onChange={async e => { try { await api.put(`/funding/plans/${plan.id}`, { releasePeriod: e.target.value }); reloadPlan(); } catch (err) { toast.error(errMsg(err)); } }}>
            {RELEASE.map(r => <option key={r[0]} value={r[0]}>{r[0] === 'none' ? 'Released upfront' : `Released ${r[1].toLowerCase()}`}</option>)}
          </select>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={publish} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${plan.is_published ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
            {plan.is_published ? <Unlock size={12} /> : <Lock size={12} />} {plan.is_published ? 'Published' : 'Draft'}
          </button>
          <button onClick={deletePlan} title="Delete plan" className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"><Trash2 size={12} /> Delete</button>
        </div>
      </div>

      {/* Service budgets */}
      <div className="flex items-center justify-between mb-2 mt-5">
        <h4 className="text-sm font-semibold text-slate-700">Service budgets</h4>
        <button onClick={() => setAddOpen(true)} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700"><Plus size={13} /> Add service</button>
      </div>
      {(plan.lines || []).length === 0 ? (
        <p className="text-sm text-slate-400 py-3">No service budgets yet. Add one per support (Personal Care, Community Access…) with its budget — shifts then auto-bill against it.</p>
      ) : (
        <div className="space-y-2">
          {plan.lines.map(l => (
            <ServiceLine key={l.id} plan={plan} line={l} onSaved={reloadPlan} onDelete={delLine} />
          ))}
        </div>
      )}
      {/* Funding releases (installments) — when the plan releases budget in periods */}
      {plan.installments && plan.installments.length > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Funding releases <span className="text-[11px] text-slate-400 font-normal">— {(RELEASE.find(r => r[0] === plan.release_period) || [])[1] || ''} installments of the plan budget</span></h4>
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5 font-semibold w-8">#</th>
                  <th className="text-left px-3 py-1.5 font-semibold">Period</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Released</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Committed</th>
                  <th className="text-right px-3 py-1.5 font-semibold">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {plan.installments.map(inst => (
                  <tr key={inst.index} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 text-slate-400">{inst.index}</td>
                    <td className="px-3 py-1.5 text-slate-600">{inst.start} → {inst.end}</td>
                    <td className="px-3 py-1.5 text-right font-medium text-slate-700">{money(inst.amount)}</td>
                    <td className="px-3 py-1.5 text-right text-slate-600">{money(inst.committed)}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${Number(inst.remaining) < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{money(inst.remaining)}{Number(inst.remaining) < 0 && <span className="ml-1 text-[9px] uppercase">over</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Shifts dated in a period draw from that release. A red “over” means that installment is over-committed — the scheduler flags shifts that exceed it.</p>
        </div>
      )}
      {addOpen && <AddLineModal plan={plan} onClose={() => setAddOpen(false)} onAdded={() => { setAddOpen(false); reloadPlan(); }} />}
    </Modal>
  );
}

// One funded service: a collapsed summary row that expands into an inline editor for the budget,
// opening "used", the per-day/time rate overrides (default from the price book) and its period.
const BANDS = [
  ['weekday', 'Weekday Daytime', 'weekday_rate', 'weekday_code'],
  ['evening', 'Weekday Evening', 'evening_rate', 'evening_code'],
  ['night', 'Weekday Night', 'night_rate', 'night_code'],
  ['saturday', 'Saturday', 'saturday_rate', 'saturday_code'],
  ['sunday', 'Sunday', 'sunday_rate', 'sunday_code'],
  ['public_holiday', 'Public Holiday', 'public_holiday_rate', 'public_holiday_code'],
];
const BAND_SHORT = { weekday: 'Wkdy', evening: 'Eve', night: 'Night', saturday: 'Sat', sunday: 'Sun', public_holiday: 'PH' };
// An SIL line (registration group 0115, or named for SIL) can claim Weekly instead of Hourly.
const isSilLine = (l) => String(l.support_item_number || '').split('_')[2] === '0115'
  || /independent living|(^|[^a-z])sil([^a-z]|$)/i.test(String(l.service_type_name || ''));

function ServiceLine({ plan, line, onSaved, onDelete }) {
  const [open, setOpen] = useState(false);
  const [silOpen, setSilOpen] = useState(false);
  const sil = isSilLine(line);
  const weekly = line.claiming_method === 'weekly';
  const [budget, setBudget] = useState(line.budget ?? '');
  const [used, setUsed] = useState(line.used_to_date ?? '');
  // Default the service period to the plan's (total funds) period — not a required field.
  const [startDate, setStartDate] = useState(fmtD(line.start_date) || fmtD(plan.start_date) || '');
  const [endDate, setEndDate] = useState(fmtD(line.end_date) || fmtD(plan.end_date) || '');
  const ov = line.rate_overrides || {};
  const [rates, setRates] = useState(() => Object.fromEntries(BANDS.map(([k, , bk]) => [k, ov[k] != null ? String(ov[k]) : (line[bk] != null ? String(line[bk]) : '')])));
  // Per-band funding approval — a band is approved unless explicitly set false. Untick = client not funded.
  const ab = line.approved_bands || {};
  const [approved, setApproved] = useState(() => Object.fromEntries(BANDS.map(([k]) => [k, ab[k] !== false])));
  // Who pays for THIS budget when it differs from the plan (the NDIS lets each support category be
  // managed its own way: SIL NDIA-managed, community plan-managed), and how often it is invoiced.
  const [payBy, setPayBy] = useState(line.managed_by || '');
  const [billTo, setBillTo] = useState(line.invoice_to || '');
  const [every, setEvery] = useState(line.invoice_every || '');
  const [anchor, setAnchor] = useState(fmtD(line.invoice_anchor) || fmtD(line.start_date) || fmtD(plan.start_date) || '');
  const payer = payBy || plan.managed_by || 'ndia_managed';
  const [saving, setSaving] = useState(false);
  const drawdown = Math.max(0, (Number(line.utilised) || 0) - (Number(line.used_to_date) || 0));
  const liveBalance = budget === '' ? null : Number(budget || 0) - (used === '' ? 0 : Number(used)) - drawdown;
  const dot = line.service_color || CAT_COLOR[line.support_category] || '#94a3b8';
  const save = async () => {
    setSaving(true);
    try {
      const rateOverrides = {};
      for (const [k, , bk] of BANDS) {
        const v = rates[k];
        if (v !== '' && v != null && Number(v) !== Number(line[bk])) rateOverrides[k] = Number(v); // only changed-from-book
      }
      await api.put(`/funding/plans/${plan.id}/lines/${line.id}`, {
        budget: budget === '' ? null : Number(budget),
        usedToDate: used === '' ? 0 : Number(used),
        startDate: startDate || '', endDate: endDate || '',
        rateOverrides,
        approvedBands: Object.fromEntries(BANDS.map(([k]) => [k, !!approved[k]])),
        ...(line.budget != null || budget !== '' ? {
          managedBy: payBy, invoiceTo: payer === 'plan_managed' ? billTo : '',
          invoiceEvery: payer === 'ndia_managed' ? '' : every, invoiceAnchor: payer !== 'ndia_managed' && every === 'fortnight' ? anchor : '',
        } : {}),
      });
      toast.success('Saved'); setOpen(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-slate-50/50" onClick={() => setOpen(o => !o)}>
        <ChevronRight size={14} className={`text-slate-300 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-700 text-sm truncate">{line.service_type_name}</p>
          <p className="text-[10px] text-slate-400 truncate">
            {line.support_item_number && <span className="font-mono text-indigo-500">{line.support_item_number}</span>}
            {line.support_item_number && (line.support_category || line.weekday_rate != null) ? ' · ' : ''}
            {(line.support_category || '').replace(/_/g, ' ')}{line.weekday_rate != null ? ` · ${money(line.weekday_rate)}/h wkdy` : ''}
          </p>
          {(() => {
            const off = BANDS.filter(([k]) => (line.approved_bands || {})[k] === false).map(([k]) => BAND_SHORT[k]);
            return off.length ? <span className="inline-flex items-center gap-1 mt-1 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">Not funded: {off.join(', ')}</span> : null;
          })()}
          {sil && (
            <span className={`inline-flex items-center gap-1 mt-1 ml-1 text-[9px] font-semibold rounded-full px-1.5 py-0.5 border ${weekly ? 'text-teal-800 bg-teal-50 border-teal-200' : 'text-slate-600 bg-slate-50 border-slate-200'}`}>
              {weekly ? 'SIL claimed weekly' : 'SIL claimed hourly'}
            </span>
          )}
          {(line.managed_by && line.managed_by !== plan.managed_by) || line.invoice_every ? (
            <span className="inline-flex items-center gap-1 mt-1 ml-1 text-[9px] font-semibold rounded-full px-1.5 py-0.5 border text-violet-800 bg-violet-50 border-violet-200">
              {[line.managed_by && line.managed_by !== plan.managed_by ? MANAGED_LABEL[line.managed_by] : null,
                line.invoice_every ? `invoiced ${{ week: 'weekly', fortnight: 'fortnightly', month: 'monthly' }[line.invoice_every]}` : null,
                (line.managed_by || plan.managed_by) === 'plan_managed' && line.invoice_to ? `to ${line.invoice_to}` : null].filter(Boolean).join(' · ')}
            </span>
          ) : null}
          {weekly && line.sil_weekly_hours && (() => {
            const h = line.sil_weekly_hours;
            const parts = [['weekday', 'wkday'], ['evening', 'eve'], ['night', 'night'], ['saturday', 'Sat'], ['sunday', 'Sun'], ['public_holiday', 'PH'], ['sleepover', 'sleepover']]
              .filter(([k]) => Number(h[k]) > 0).map(([k, label]) => `${Number(h[k])} ${label}`);
            return parts.length ? <p className="text-[10px] text-slate-500 mt-0.5">Roster of care, per week: {parts.join(' · ')}</p> : null;
          })()}
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-slate-700">{line.balance != null ? money(line.balance) : '—'}</p>
          <p className="text-[10px] text-slate-400">{line.budget != null ? `of ${money(line.budget)}` : 'from pool'}</p>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete(line); }} title="Remove service" className="text-slate-300 hover:text-red-500 shrink-0 ml-1"><Trash2 size={13} /></button>
      </div>
      {open && (
        <div className="px-4 py-3 bg-slate-50/50 border-t border-slate-100 space-y-3">
          {sil && (
            <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 ${weekly ? 'border-teal-200 bg-teal-50/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-slate-800">Claiming method: {weekly ? 'Weekly' : 'Hourly'}</p>
                <p className="text-[11px] text-slate-500">{weekly ? 'One claim per Monday to Sunday week; SIL shifts show $0.00, covered by the weekly claim.' : 'Each approved SIL shift is claimed by the hour. SIL can be claimed weekly instead.'}</p>
              </div>
              <button type="button" className={weekly ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'} onClick={() => setSilOpen(true)}>
                {weekly ? 'Weekly amount and schedule' : 'Set up weekly claiming'}
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Budget ($)" hint="Blank = draw from the shared pool."><input type="number" className="input-field" value={budget} onChange={e => setBudget(e.target.value)} /></F>
            <F label="Already used ($)" hint="Spend before this system."><input type="number" className="input-field" value={used} onChange={e => setUsed(e.target.value)} /></F>
          </div>
          {liveBalance != null && (
            <div className="flex items-center justify-between rounded-xl bg-white border border-slate-100 px-3 py-2 text-sm">
              <span className="text-slate-500">Balance{drawdown > 0 ? ` (after ${money(drawdown)} from shifts)` : ''}</span>
              <span className={`font-bold ${liveBalance < 0 ? 'text-rose-600' : 'text-slate-800'}`}>{money(liveBalance)}</span>
            </div>
          )}
          {(line.budget != null || budget !== '') ? (
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 space-y-2.5">
              <p className="text-xs font-medium text-slate-600">Who pays for this budget <span className="text-[10px] text-slate-400 font-normal">— only if it differs from the plan ({MANAGED_LABEL[plan.managed_by] || 'NDIA-managed'}). E.g. SIL NDIA-managed, community plan-managed.</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <F label="Paid by">
                  <select className="input-field" value={payBy} onChange={(e) => setPayBy(e.target.value)}>
                    <option value="">Same as the plan ({MANAGED_LABEL[plan.managed_by] || 'NDIA-managed'})</option>
                    {MANAGED.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </F>
                {payer === 'plan_managed' && (
                  <F label="Plan manager (invoice to)" hint="As the contact is named in Xero.">
                    <input className="input-field" value={billTo} placeholder={plan.invoice_to || 'e.g. NDSP Plan Managers'} onChange={(e) => setBillTo(e.target.value)} />
                  </F>
                )}
                {payer !== 'ndia_managed' && (
                  <F label="Invoice every">
                    <select className="input-field" value={every} onChange={(e) => setEvery(e.target.value)}>
                      <option value="">Usual (monthly, or weekly with SIL weekly)</option>
                      <option value="week">Week (Monday to Sunday)</option>
                      <option value="fortnight">Fortnight</option>
                      <option value="month">Month</option>
                    </select>
                  </F>
                )}
                {payer !== 'ndia_managed' && every === 'fortnight' && (
                  <F label="First fortnight starts" hint="Its Monday; each invoice covers two weeks from there.">
                    <input type="date" className="input-field" value={anchor} onChange={(e) => setAnchor(e.target.value)} />
                  </F>
                )}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-slate-500">This service draws on the plan's shared pool, so it is paid as the plan is ({MANAGED_LABEL[plan.managed_by] || 'NDIA-managed'}). Give it a budget to set its own payer.</p>
          )}
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1.5">Day / time rates <span className="text-[10px] text-slate-400 font-normal">— from the price book. Some NDIS supports are flat-rate, so blank bands bill at the Weekday rate. Override to charge below the cap. <span className="text-rose-400">Untick a band to mark the client NOT funded for it — those shifts won't be billed and the scheduler will warn.</span></span></p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {BANDS.map(([k, label, bk, ck]) => {
                const ownRate = line[bk];
                const inherits = ownRate == null && line.weekday_rate != null; // flat-rate support → bills at the weekday rate on every day
                const on = approved[k];
                return (
                <div key={k} className={on ? '' : 'opacity-70'}>
                  <div className="flex items-center justify-between gap-1 mb-0.5">
                    <button type="button" onClick={() => setApproved(a => ({ ...a, [k]: !a[k] }))}
                      title={on ? 'Funded — click to mark client NOT approved for this band' : 'Not approved — click to enable'}
                      className="flex items-center gap-1 min-w-0">
                      <span className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center shrink-0 ${on ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-300'}`}>
                        {on && <Check size={9} strokeWidth={3} />}
                      </span>
                      <label className="text-[10px] font-medium text-slate-600 cursor-pointer truncate">{label}</label>
                    </button>
                    {on
                      ? (line[ck] ? <span className="font-mono text-[9px] text-slate-400 truncate" title={`NDIS item ${line[ck]}`}>{line[ck]}</span>
                          : inherits ? <span className="text-[9px] text-slate-300 italic">= weekday</span> : null)
                      : <span className="text-[9px] text-rose-500 font-semibold shrink-0">not funded</span>}
                  </div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">$</span>
                    <input type="number" disabled={!on}
                      className={`input-field pl-5 py-1.5 text-sm ${on ? '' : 'bg-slate-100 text-slate-300 line-through cursor-not-allowed'}`}
                      placeholder={!on ? 'not funded' : (ownRate != null ? String(ownRate) : (inherits ? String(line.weekday_rate) : '—'))}
                      value={on ? rates[k] : ''} onChange={e => setRates(r => ({ ...r, [k]: e.target.value }))} />
                  </div>
                </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Service start" hint="Optional — defaults to the plan period."><input type="date" className="input-field" value={startDate} onChange={e => setStartDate(e.target.value)} /></F>
            <F label="Service end" hint="Optional — defaults to the plan period."><input type="date" className="input-field" value={endDate} onChange={e => setEndDate(e.target.value)} /></F>
          </div>
          <div className="flex justify-end gap-2"><button onClick={() => setOpen(false)} className="btn-secondary btn-sm">Close</button><button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : 'Save'}</button></div>
        </div>
      )}
      {silOpen && <SilWeeklyDialog lineId={line.id} clientId={plan.client_id} onClose={() => setSilOpen(false)} onSaved={onSaved} />}
    </div>
  );
}

// Add a service budget by picking a support straight from the price guide (name + rate shown).
function AddLineModal({ plan, onClose, onAdded }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);   // { supportItemNumber, supportItemName, weekdayRate }
  const [budget, setBudget] = useState('');
  // Default the service period to the plan's (total funds) period.
  const [startDate, setStartDate] = useState(fmtD(plan.start_date) || '');
  const [endDate, setEndDate] = useState(fmtD(plan.end_date) || '');
  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState('hourly');
  const [silLineId, setSilLineId] = useState(null);
  const pickedSil = picked && isSilLine({ support_item_number: picked.supportItemNumber, service_type_name: picked.supportItemName });
  useEffect(() => { if (picked) setClaiming(/_821_/.test(picked.supportItemNumber || '') || /weekly/i.test(picked.supportItemName || '') ? 'weekly' : 'hourly'); }, [picked]);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const id = setTimeout(() => {
      const pb = plan.price_book_id ? `&priceBookId=${plan.price_book_id}` : '';
      api.get(`/price-books/items?search=${encodeURIComponent(q)}${pb}`).then(r => setResults(r.data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q, plan.price_book_id]);
  const add = async () => {
    if (!picked) return toast.error('Pick a support from the price guide');
    setSaving(true);
    try {
      const { data } = await api.post(`/funding/plans/${plan.id}/lines`, { supportItemNumber: picked.supportItemNumber, serviceTypeName: picked.supportItemName, budget: budget || null, startDate: startDate || null, endDate: endDate || null });
      toast.success('Service budget added');
      if (pickedSil && claiming === 'weekly') {
        const mine = (data?.lines || []).filter((l) => l.support_item_number === picked.supportItemNumber)
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0];
        if (mine) { setSilLineId(mine.id); return; }
      }
      onAdded();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Add Service Budget" onClose={onClose} wide>
      <F label="Support (from the price guide)" hint="Search the NDIS price guide — the rate flows automatically.">
        {picked ? (
          <div className="flex items-center gap-2 border border-cyan-200 bg-cyan-50 rounded-xl px-3 py-2">
            <div className="flex-1 min-w-0"><p className="text-sm text-slate-700 leading-snug">{picked.supportItemName}</p><p className="text-[11px] text-indigo-500 font-mono mt-0.5">{picked.supportItemNumber}{picked.weekdayRate != null ? ` · $${picked.weekdayRate.toFixed(2)}/h` : ''}</p></div>
            <button onClick={() => setPicked(null)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
          </div>
        ) : (
          <div className="relative">
            <input className="input-field" placeholder="Search e.g. 'self-care', 'community', code…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
            {results.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-auto">
                {results.map(it => (
                  <button key={it.supportItemNumber} type="button" onClick={() => { setPicked(it); setQ(''); setResults([]); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                    <div className="text-sm text-slate-700 leading-snug">{it.supportItemName}</div>
                    <div className="text-[11px] mt-0.5 flex items-center justify-between gap-2"><span className="font-mono text-indigo-500">{it.supportItemNumber}</span>{it.weekdayRate != null && <span className="text-emerald-600 font-semibold shrink-0">${it.weekdayRate.toFixed(2)}/h</span>}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </F>
      <F label="Budget ($) — optional">
        <input type="number" className="input-field" value={budget} onChange={e => setBudget(e.target.value)} placeholder="Leave blank to draw from the shared pool" />
        {(() => {
          const planBudget = Number(plan.summary?.budget ?? plan.budget ?? 0);
          const allocated = Number(plan.summary?.allocated ?? 0);
          const pool = Math.max(0, planBudget - allocated);
          const b = Number(budget || 0);
          if (budget !== '' && b > 0) {
            const over = b > pool;
            return <p className={`text-[11px] mt-1 ${over ? 'text-rose-600 font-medium' : 'text-slate-500'}`}>Carves {money(b)} out of the plan — {over ? `but only ${money(pool)} is still unallocated` : `${money(pool - b)} stays in the shared pool`}.</p>;
          }
          return <p className="text-[11px] text-emerald-600 mt-1">No budget — draws from the shared plan pool ({money(pool)} available). Nothing is carved out of the {money(planBudget)} plan.</p>;
        })()}
      </F>
      {pickedSil && (
        <F label="Claiming method" hint={claiming === 'weekly' ? 'One claim per Monday to Sunday week. Next you add the service agreement and check the weekly amount.' : 'Each approved SIL shift is claimed by the hour.'}>
          <div className="flex gap-2">
            {[['weekly', 'Weekly'], ['hourly', 'Hourly']].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setClaiming(v)} aria-pressed={claiming === v}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${claiming === v ? 'border-teal-300 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white text-slate-600'}`}>{label}</button>
            ))}
          </div>
        </F>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Service start — optional"><input type="date" className="input-field" value={startDate} onChange={e => setStartDate(e.target.value)} /></F>
        <F label="Service end — optional"><input type="date" className="input-field" value={endDate} onChange={e => setEndDate(e.target.value)} /></F>
      </div>
      <div className="flex justify-end gap-2 pt-2"><button onClick={onClose} className="btn-secondary btn-sm">Cancel</button><button onClick={add} disabled={saving || !picked} className="btn-primary btn-sm">{saving ? 'Adding…' : pickedSil && claiming === 'weekly' ? 'Add and set up weekly' : 'Add'}</button></div>
      {silLineId && <SilWeeklyDialog lineId={silLineId} clientId={plan.client_id} initialMethod="weekly" onClose={() => { setSilLineId(null); onAdded(); }} onSaved={() => {}} />}
    </Modal>
  );
}

function Modal({ title, onClose, children, wide, xwide }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${xwide ? 'max-w-5xl' : wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">{title}</h3><button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button></div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
function F({ label, children, hint }) { return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}{hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}</div>; }
